import React, { useCallback, useEffect, useState, Fragment } from 'react'
import { connect } from 'react-redux'
import moment from 'moment'
import queryString from 'query-string'
import DataTable from '../../../../../app/components/statelessDataTable'
import DefaultErrorMessage from '../../../../../common/components/ErrorBoundary/errorMessage'
import createColumns from '../columns/columns'
import service from '../../service'
import { hasTipped } from '../../../../../account/selectors'
import { useLocations } from '../../../../../context/locationsContext'
import queryJurisdictionWithMultipleTippedWages from '../../../statelessLocationTable/service/queryJurisdictionsWithMultipleTippedWages'
import { exportCsv, handlePagination } from '../../../../../common/helpers'
import { SESSION_STORAGE_KEYS } from '../../../../../app/enums'
import useSessionStorage from '../../../../../hooks/useSessionStorage'
import MaintenanceBanner from '../../../../../account/components/maintenance/maintenanceBanner'
import CsvSpinner from './csvSpinner'
import { formatDisplayName, formattedDate } from '../../../../../common/utils/csvUtility'
import { CSVDownloadError } from '../../../../../common/components/CSVDownloadError/CSVDownloadError'

const MinWageNotifications = ({ tipped, notifications, notificationsError, location, wagesByJurisdiction, companyDisplayName }) => {
  if (notificationsError) throw new Error('Notifications can not load from API')
  const { locationsData, legacyMWNotificationsArray } = useLocations()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState([])
  const [errorPopupOpen, setErrorPopupOpen] = useState(false)
  const [error, setError] = useState(false)
  const [total, setTotal] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [sortOptions, setSortOptions] = useState({
    field: 'notificationDate',
    direction: 'asc'
  })
  const [filters, setFilters] = useState({ state: '' })
  const queryParams = queryString.parse(location.search)
  const initialFilterOptions = {
    filter: '',
    field: '',
    date: {
      startDate: moment()
        .format('MM/DD/YYYY'),
      endDate: moment()
        .add(90, 'days')
        .format('MM/DD/YYYY')
    }
  }
  const { getFromSessionStorage } = useSessionStorage()
  const [ignoreMaintenanceBannerFlag, setIgnoreMaintenanceBannerFlag] = useState()
  const [creatingCSV, setCreatingCSV] = useState(false)

  let globallySubscribed = true

  const handleClose = () => setErrorPopupOpen(false)

  const ignoreMaintenanceBannerKey = SESSION_STORAGE_KEYS.IGNORE_MAINTENANCE_BANNER
  const sessionIgnoreMaintenanceBanner = JSON.parse(sessionStorage.getItem(ignoreMaintenanceBannerKey))

  useEffect(() => {
    setIgnoreMaintenanceBannerFlag(sessionIgnoreMaintenanceBanner)
    return (() => (globallySubscribed = false))
  }, [])

  if (queryParams && queryParams.dateRange !== undefined) {
    initialFilterOptions.date = {
      startDate: moment()
        .format('MM/DD/YYYY'),
      endDate: moment()
        .add(queryParams.dateRange, 'days')
        .format('MM/DD/YYYY')
    }
  }
  const [filterOptions, setFilterOptions] = useState(initialFilterOptions)

  const [columns, setColumns] = useState(createColumns({
    sortOptions,
    filterOptions,
    eventHandlers: {
      setSortOptions,
      setFilterOptions,
      setPageNumber
    }
  }))

  // FOR MAINTENANCE BANNER
  const [maintenanceBanner, setMaintenanceBanner] = useState(false)

  const rowsPerPage = 20

  // eslint-disable-next-line func-style
  async function getJurisdictionsWithMultipleWages() {
    const jurisdictionsWithMultipleTippedWages = await queryJurisdictionWithMultipleTippedWages()
    return jurisdictionsWithMultipleTippedWages.map((jurisdiction) => jurisdiction.name)
  }

  const assignMultipleTippedFlag = (jurisdictionsWithMultipleTippedWages, notification) => {
    // NOTE: Leaving the tipWage in for reference
    const tipWage = notification.notificationNewTipWage || notification.notificationPrevailingTipWage || {}
    return Object.assign(notification, { hasMultipleTippedWages: jurisdictionsWithMultipleTippedWages.includes(notification.name) })
  }

  // eslint-disable-next-line func-style
  function sortAndFilterData() {
    if (globallySubscribed) setLoading(true)
    // FOR MAINTENANCE BANNER
    const notificationsData = legacyMWNotificationsArray
    const wagesByJdFromRedux = wagesByJurisdiction
    const mappedNotifications = service.mapNotifications(tipped, notificationsData, locationsData, wagesByJdFromRedux)
    if (mappedNotifications && mappedNotifications.length > 0) {
      getJurisdictionsWithMultipleWages()
        .then((jurisdictionsWithMultipleTippedWages) => {
          if (globallySubscribed) {
            mappedNotifications.map((notification) => {
              assignMultipleTippedFlag(jurisdictionsWithMultipleTippedWages, notification)
            })
            setLoading(false)
          }
        })
        .catch((getJurisdictionsWithMultipleWagesError) => {
          console.error(getJurisdictionsWithMultipleWagesError.message || 'error while trying to retrieve jurisdictions with multiple tipped wages')
          if (globallySubscribed) {
            setLoading(false)
            setError(getJurisdictionsWithMultipleWagesError)
          }
        })
    }

    const notificationsFilteredByDate = filterOptions.date
      ? mappedNotifications.filter((notification) => moment(notification.notificationDate)
        .isSameOrBefore(filterOptions.date.endDate)
        && moment(notification.notificationDate)
          .isSameOrAfter(filterOptions.date.startDate))
      : mappedNotifications

    const notificationsFilteredBySearch = searchText === ''
      ? notificationsFilteredByDate
      : notificationsFilteredByDate.filter((notification) => notification.name.toLowerCase()
        .includes(searchText.toLowerCase()))
    let sortedNotifications = []
    if (sortOptions.field === 'locations') {
      sortedNotifications = _.orderBy(
        notificationsFilteredBySearch,
        [(notification) => notification[sortOptions.field] || 0],
        [sortOptions.direction]
      )
    } else if (sortOptions.field === 'notificationDate') {
      sortedNotifications = _.orderBy(
        notificationsFilteredBySearch,
        [(notification) => new Date(notification.notificationDate)],
        [sortOptions.direction]
      )
    } else {
      sortedNotifications = _.orderBy(
        notificationsFilteredBySearch,
        sortOptions.field,
        [sortOptions.direction]
      )
    }
    return {
      notificationsFilteredBySearch,
      sortedNotifications
    }
  }

  useEffect(() => {
    let subscribed = true
    if (locationsData && notifications) {
      const { notificationsFilteredBySearch, sortedNotifications } = sortAndFilterData()
      const paginatedNotifications = handlePagination(sortedNotifications, rowsPerPage, pageNumber)
      const columns = createColumns({
        tipped,
        sortOptions,
        filterOptions,
        eventHandlers: {
          setSortOptions,
          setFilterOptions,
          setPageNumber
        }
      })
      if (subscribed) {
        setData(paginatedNotifications)
        setTotal(notificationsFilteredBySearch.length)
        setColumns(columns)
      }
    }

    return () => (subscribed = false)
  }, [locationsData, notifications, pageNumber, filters, searchText, sortOptions, filterOptions])

  const handleChangePage = useCallback((event) => {
    if (globallySubscribed) {
      setPageNumber(event.selected + 1)
      setData([])
      setLoading(true)
    }
  }, [])

  const handleSetSearchText = useCallback((value) => {
    if (globallySubscribed) {
      setSearchText(value)
    }
  }, [])

  const handleSetSortOptions = useCallback((value) => {
    if (globallySubscribed) {
      setSortOptions(value)
    }
  }, [])

  const handleSetFilters = (value) => {
    if (globallySubscribed) {
      setFilters(value)
      setPageNumber(1)
    }
  }

  const handleSetFilterOptions = (newValue) => {
    if (globallySubscribed) {
      setFilterOptions(newValue)
    }
  }

  const handleSetPageNumber = useCallback((value) => {
    if (globallySubscribed) {
      setPageNumber(value)
    }
  }, [])

  const handleExportCsv = async () => {
    if (globallySubscribed) setLoading(true)
    setCreatingCSV(true)
    try {
      const { sortedNotifications } = sortAndFilterData()

      /*
        The setTimeout here is a temporary fix to get a CSV showing See location details
        Created a ticket for the backlog ELM-2416 to look at this in further depth when time allows

        setTimeout is allowing time for the sortedNotifications to be finshed updating in getJurisdictionsWithMultipleWages
        part of the sortAndFilterData function

        Ideally we will also remove the <CsvSpinner />
      */
      setTimeout(() => {
        if (sortedNotifications.length > 0) {
          const csvRows = service.createNotificationsCsvRows(tipped, sortedNotifications)
          const csvFilename = `${formatDisplayName(companyDisplayName)}_Minimum_Wage_Notifications_${formattedDate()}.csv`
          exportCsv(csvRows, csvFilename)
          setCreatingCSV(false)
        } else {
          setCreatingCSV(false)
          setErrorPopupOpen(true)
        }
      }, 2500)
    } catch (error) {
      setCreatingCSV(false)
      setErrorPopupOpen(true)
      console.error('Error while generating csv file ', error)
    }
    if (globallySubscribed) setLoading(false)
  }

  return (
    error
      ? <DefaultErrorMessage error={error} data-testid="error-container" />
      : maintenanceBanner && !ignoreMaintenanceBannerFlag
        ? <MaintenanceBanner />
        : creatingCSV
          ? <CsvSpinner />
          : (
            <Fragment>
              <DataTable
                {...{
                  pageCount: Math.ceil(total / rowsPerPage),
                  isLoading: loading,
                  recordType: 'Minimum Wage Notifications',
                  showSearch: true,
                  showExport: true,
                  showLocaleFilter: false,
                  showDateFilter: true,
                  eventHandlers: {
                    handleChangePage,
                    setSearchText: handleSetSearchText,
                    setSortOptions: handleSetSortOptions,
                    setFilterOptions: handleSetFilterOptions,
                    setFilters: handleSetFilters,
                    setPageNumber: handleSetPageNumber,
                    export: handleExportCsv
                  },
                  columns,
                  data,
                  filters,
                  sortOptions,
                  filterOptions,
                  pageNumber,
                  total,
                  searchText,
                  rowsPerPage
                }}
              />
              <CSVDownloadError open={errorPopupOpen} onClose={handleClose} />
            </Fragment>
          )
  )
}

const mapStateToProps = (state) => {
  const tipped = hasTipped(state)
  return {
    notifications: state.minWage.notifications,
    notificationsError: state.minWage.notificationsError,
    wagesByJurisdiction: state.minWage.wagesByJurisdiction,
    companyDisplayName: state.account.company.displayName,
    tipped
  }
}

export default connect(mapStateToProps)(MinWageNotifications)
